import { useMemo } from 'react'
import { ProjectProjectFeature } from '@/types/project-project-features'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGet, { UseGetOptions } from '../core-hooks/useGet'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'
import { getDetail } from '../request-wrappers'

const service = buildService<ProjectProjectFeature>({
  pathCategory: 'project_project_features',
})

export function useGetProjectProjectFeature({
  id,
  ...options
}: UseGetDetailOptions<ProjectProjectFeature>) {
  const query = useGetDetail<ProjectProjectFeature>({
    id,
    queryKey: queryKeys.projectProjectFeatures.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    projectProjectFeature: query.data,
    ...query,
  }
}

export function useCreateProjectProjectFeature(
  options: UseCreateDetailOptions<ProjectProjectFeature> = {},
) {
  const mutation = useCreateDetail<ProjectProjectFeature>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createProjectProjectFeature: mutation.mutate,
    createProjectProjectFeatureAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateProjectProjectFeature(
  options: UseUpdateDetailOptions<ProjectProjectFeature> = {},
) {
  const mutation = useUpdateDetail<ProjectProjectFeature>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateProjectProjectFeature: mutation.mutate,
    updateProjectProjectFeatureAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjectProjectFeature(
  options: UseDeleteDetailOptions = {},
) {
  const mutation = useDeleteDetail<ProjectProjectFeature>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteProjectProjectFeature: mutation.mutate,
    deleteProjectProjectFeatureAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetProjectProjectFeatures({
  filters,
  ...options
}: UseGetListOptions<ProjectProjectFeature> = {}) {
  const query = useGetList<ProjectProjectFeature>({
    filters,
    queryKey: queryKeys.projectProjectFeatures.list({ filters, persist: true }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    projectProjectFeatures: query.allData,
    ...query,
  }
}

export function useUpdateProjectProjectFeatures(
  options: UseUpdateListOptions<ProjectProjectFeature> = {},
) {
  const mutation = useUpdateList<ProjectProjectFeature>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateProjectProjectFeatures: mutation.mutate,
    updateProjectProjectFeaturesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteProjectProjectFeatures({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<ProjectProjectFeature>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteProjectProjectFeatures: mutation.mutate,
    deleteProjectProjectFeaturesAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Get if ProjectProjectFeatures Exist
 * Used to check if project project features exist based on filters.
 */
type ExistsResponse = {
  exists: boolean
}
export function useGetProjectProjectFeaturesExist({
  filters,
  ...options
}: UseGetOptions<ExistsResponse> = {}) {
  const updatedFilters = useMemo(
    () => ({
      'api-consistency': 'weak',
      ...filters, // Allow api-consistency to be overridden
    }),
    [filters],
  )
  const query = useGet<ExistsResponse>({
    filters: updatedFilters,
    queryKey: queryKeys.projectProjectFeatures.list(
      { filters: updatedFilters },
      ['exists'],
    ),
    serviceFn: (serviceFnOptions) => {
      return getDetail<ExistsResponse>({
        url: '/v2/pd/project_project_features/~exists',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    exists: query.data?.exists || false,
    ...query,
  }
}
